import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { homeData } from "../../data/homeData";
import { assetPath } from "../../utils/assetPath";

/* ── Animation variants ── */
const headingVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1] },
  },
};

const bodyVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] },
  },
};

const image1Variants = {
  hidden: { opacity: 0, scale: 0.88 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] },
  },
};

const image2Variants = {
  hidden: { opacity: 0, scale: 0.88 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 1.1, delay: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

const accentLineVariants = {
  hidden: { scaleY: 0, opacity: 0 },
  visible: {
    scaleY: 1,
    opacity: 1,
    transition: { duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] },
  },
};

const WhyAepl = () => {
  const { problemWesolve } = homeData;
  const sectionRef = useRef(null);

  /* Parallax for images on scroll */
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const img1Y = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const img2Y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const img1Rotate = useTransform(scrollYProgress, [0, 1], [-2, 2]);
  const img2Rotate = useTransform(scrollYProgress, [0, 1], [2, -2]);

  return (
    <section
      ref={sectionRef}
      className="py-24 md:py-40 relative  bg-[#ECF0F4]  overflow-hidden"
      id="problem"
    >
      {/* Subtle background gradient decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[60%] h-[60%] bg-linear-to-bl from-blue-50/60 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-linear-to-tr from-slate-50/80 via-transparent to-transparent rounded-full blur-3xl" />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        {/* Center-aligned heading */}
        <motion.div
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-16 md:mb-20"
        >
          <h2 className="text-4xl md:text-5xl lg:text-7xl font-semibold text-slate-800 tracking-tight leading-[1.1] text-balance mx-auto max-w-[1200px]">
            {problemWesolve.heading}
          </h2>
        </motion.div>

        {/* Two-column: text left, images right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-start">
          {/* Left Text Column */}
          <div className="lg:col-span-5 flex flex-col items-start">
            <motion.div
              variants={bodyVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="flex gap-6 max-w-[500px]"
            >
              {/* Vertical line accent  animated scale */}
              <motion.div
                variants={accentLineVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                className="w-[3px] bg-linear-to-b from-blue-400 via-slate-300 to-slate-200 shrink-0 self-stretch rounded-full origin-top"
              />
              <div className="flex flex-col gap-10 py-2">
                <p className="text-slate-600 text-[15px] md:text-[17px] leading-relaxed font-medium">
                  {problemWesolve.description}
                </p>
                <motion.p
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.7,
                    delay: 0.5,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="text-slate-800 font-bold text-lg md:text-xl leading-snug tracking-tight"
                >
                  {problemWesolve.conclusion}
                </motion.p>
                <motion.p
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.7,
                    delay: 0.65,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="text-slate-500 font-semibold text-sm leading-snug max-w-[400px]"
                >
                  {problemWesolve.callToAction}
                </motion.p>
              </div>
            </motion.div>
          </div>

          {/* Right Image Column (Staggered Layout with Parallax) */}
          <div className="lg:col-span-7 relative w-full min-h-[280px] h-[350px] sm:h-[450px] md:h-[700px] mt-16 lg:mt-0">
            {/* Image 1: Top Left  with scroll parallax */}
            <motion.div
              variants={image1Variants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              style={{ y: img1Y, rotate: img1Rotate, translateZ: 0, backfaceVisibility: "hidden" }}
              whileHover={{ scale: 1.03, transition: { duration: 0.4 } }}
              className="absolute top-0 left-0 w-[55%] md:w-[50%] h-[220px] sm:h-[300px] md:h-[550px] z-10 cursor-pointer will-change-transform"
            >
              <div className="w-[80%]  h-[85%] rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.15)] group">
                <img
                  src={assetPath("/HomePage/WhyAepl/Pic1.webp")}
                  alt="Aviation Ground View"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
              </div>
              {/* Floating glow behind image */}
              <div className="absolute -inset-4 bg-blue-200/30 rounded-3xl blur-2xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </motion.div>

            {/* Image 2: Bottom Right, Overlapping  with scroll parallax */}
            <motion.div
              variants={image2Variants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              style={{ y: img2Y, rotate: img2Rotate, translateZ: 0, backfaceVisibility: "hidden" }}
              whileHover={{ scale: 1.03, transition: { duration: 0.4 } }}
              className="absolute bottom-0 right-0 w-[60%] md:w-[55%] h-[250px] sm:h-[320px] md:h-[600px] z-20 cursor-pointer will-change-transform"
            >
              <div className="w-[80%]  h-[85%]  rounded-2xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.25)] group">
                <img
                  src={assetPath("/HomePage/WhyAepl/Pic2.webp")}
                  alt="Aviation Window View"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyAepl;
